import React, { memo, useState } from 'react';
import { mod } from 'react-swipeable-views-core';
import { virtualize } from 'react-swipeable-views-utils';
import SwipeableViews from 'react-swipeable-views';
import { isMobile } from 'react-device-detect';
import { ArrowButton } from 'components/RowList';
import withLazyStyle from 'components/LazyStyle';
import useInvokeOnce from 'hooks/useInvokeOnce';
import type { Collection, List, Normalized } from 'lib/LobbyEntities';
import type { Mapping } from 'components/DynamicLobby/src/Builder';
import CarouselPagination from './components/CarouselPagination';
import slideRenderer from './helpers/slideRenderer';
import useOffersCarouselAnalytics from './hooks/useOffersCarouselAnalytics';
import style from './offersCarousel.css?lazy';

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

// Style objects are used for react-swipeable-views. No options for using css classes
const rootStyle = isMobile
  ? { padding: '0 calc(var(--horizontalSpacing) - 4px)' }
  : { padding: '0 12% 0 0' };

const slideStyle = isMobile ? { padding: '0 4px' } : { padding: '0 8px 0 0' };

interface OffersCarouselProps {
  list: List;
  collection: Normalized<Collection>;
  mapping: Mapping;
  trackSectionScrolled: () => void;
}

const OffersCarousel = ({
  list,
  collection,
  mapping,
  trackSectionScrolled
}: OffersCarouselProps) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const trackSectionOnce = useInvokeOnce(trackSectionScrolled);
  const trackSlideChanged = useOffersCarouselAnalytics({ list, collection });
  const totalItemsCount = collection.totalElements;

  const handleChangeIndex = (index: number) => {
    trackSlideChanged({ prevIndex: slideIndex, newIndex: index });
    setSlideIndex(index);
  };

  return (
    <>
      <div className="offersCarousel">
        <VirtualizeSwipeableViews
          index={slideIndex}
          style={rootStyle}
          slideStyle={slideStyle}
          hysteresis={0.2}
          onChangeIndex={handleChangeIndex}
          onTransitionEnd={trackSectionOnce}
          slideRenderer={slideRenderer(list, mapping)}
        />

        <ArrowButton
          direction="left"
          className="offersCarousel--arrowButton offersCarousel--arrowButton--left"
          handleClick={() => handleChangeIndex(slideIndex - 1)}
        />
        <ArrowButton
          direction="right"
          className="offersCarousel--arrowButton offersCarousel--arrowButton--right"
          handleClick={() => handleChangeIndex(slideIndex + 1)}
        />
      </div>

      <CarouselPagination
        className="offersCarousel--carouselPagination"
        currentPage={mod(slideIndex, totalItemsCount)}
        totalPages={totalItemsCount}
        onPageChange={handleChangeIndex}
      />
    </>
  );
};

export default memo(withLazyStyle(style)(OffersCarousel));
